import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CreditCard, Banknote, QrCode, Receipt, DollarSign, ShieldAlert, Sparkles } from "lucide-react";

const PAYMENT_OPTIONS = [
  { id: "pix", label: "Pix", icon: QrCode },
  { id: "credito", label: "Cartão de Crédito", icon: CreditCard },
  { id: "debito", label: "Cartão de Débito", icon: CreditCard },
  { id: "boleto", label: "Boleto", icon: Receipt },
  { id: "dinheiro", label: "Dinheiro", icon: Banknote },
];

export type PriceDisclosureMode = "free" | "value_first" | "never";

const PRICE_MODE_OPTIONS: {
  value: PriceDisclosureMode;
  label: string;
  description: string;
  icon: typeof DollarSign;
}[] = [
  {
    value: "free",
    label: "Pode informar livremente",
    description: "O agente responde sobre preço sempre que o cliente perguntar.",
    icon: DollarSign,
  },
  {
    value: "value_first",
    label: "Agregar valor antes do preço",
    description:
      "Antes de citar valores, o agente apresenta ficha técnica e benefícios da marca.",
    icon: Sparkles,
  },
  {
    value: "never",
    label: "Nunca informar preço",
    description:
      "O agente não revela valores e redireciona para o canal de vendas configurado.",
    icon: ShieldAlert,
  },
];

interface PaymentSectionProps {
  paymentMethods: string[];
  onPaymentMethodsChange: (methods: string[]) => void;
  installments: string;
  onInstallmentsChange: (value: string) => void;
  priceDisclosureMode: PriceDisclosureMode;
  onPriceDisclosureModeChange: (value: PriceDisclosureMode) => void;
  priceDisclosureCondition: string;
  onPriceDisclosureConditionChange: (value: string) => void;
}

export function PaymentSection({
  paymentMethods,
  onPaymentMethodsChange,
  installments,
  onInstallmentsChange,
  priceDisclosureMode,
  onPriceDisclosureModeChange,
  priceDisclosureCondition,
  onPriceDisclosureConditionChange,
}: PaymentSectionProps) {
  const togglePaymentMethod = (method: string) => {
    if (paymentMethods.includes(method)) {
      onPaymentMethodsChange(paymentMethods.filter((m) => m !== method));
    } else {
      onPaymentMethodsChange([...paymentMethods, method]);
    }
  };

  const showCondition =
    priceDisclosureMode === "value_first" || priceDisclosureMode === "never";

  return (
    <div className="space-y-5">
      {/* Payment Methods */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Formas de Pagamento</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {PAYMENT_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = paymentMethods.includes(option.id);

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => togglePaymentMethod(option.id)}
                className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                  isSelected
                    ? "bg-agent-primary/10 border-agent-primary text-agent-primary"
                    : "bg-muted/30 border-border hover:border-agent-primary/50"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{option.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Installments */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Opções de Parcelamento</Label>
        <Input
          value={installments}
          onChange={(e) => onInstallmentsChange(e.target.value)}
          placeholder="Ex: Em até 12x sem juros no cartão"
        />
        <p className="text-xs text-muted-foreground">
          Descreva as condições de parcelamento oferecidas
        </p>
      </div>

      {/* Price disclosure mode */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Como o agente deve tratar preços</Label>
        <RadioGroup
          value={priceDisclosureMode}
          onValueChange={(v) => onPriceDisclosureModeChange(v as PriceDisclosureMode)}
          className="grid gap-2"
        >
          {PRICE_MODE_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const selected = priceDisclosureMode === opt.value;
            return (
              <label
                key={opt.value}
                htmlFor={`price-mode-${opt.value}`}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  selected
                    ? "bg-agent-primary/10 border-agent-primary"
                    : "bg-muted/30 border-border hover:border-agent-primary/50"
                }`}
              >
                <RadioGroupItem
                  value={opt.value}
                  id={`price-mode-${opt.value}`}
                  className="mt-1"
                />
                <Icon
                  className={`w-5 h-5 mt-0.5 ${
                    selected ? "text-agent-primary" : "text-muted-foreground"
                  }`}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">{opt.label}</div>
                  <p className="text-xs text-muted-foreground">{opt.description}</p>
                </div>
              </label>
            );
          })}
        </RadioGroup>
      </div>

      {/* Optional condition */}
      {showCondition && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Condição extra para informar preço (opcional)
          </Label>
          <Textarea
            value={priceDisclosureCondition}
            onChange={(e) => onPriceDisclosureConditionChange(e.target.value)}
            placeholder="Ex: só informar valor após o cliente confirmar o CEP de entrega"
            maxLength={500}
            rows={2}
          />
          <p className="text-xs text-muted-foreground">
            O agente cumprirá essa condição antes de qualquer menção a preço.
          </p>
        </div>
      )}
    </div>
  );
}
